import { Injectable } from '@angular/core';
import { ToastrService } from 'ngx-toastr';

const GENERIC_MESSAGE = 'Something went wrong. Please try again.';
const UNREACHABLE_MESSAGE = "Couldn't reach the server. Please try again.";
const OFFLINE_MESSAGE = 'You appear to be offline. Check your connection and try again.';

@Injectable({
  providedIn: 'root',
})
export class ErrorHandlerService {
  constructor(private toastrService: ToastrService) {}

  /**
   * Turns an HTTP failure into one user-readable sentence, or '' when nothing
   * should be shown.
   *
   * Status 0 means the browser never received an HTTP response at all: the XHR
   * was aborted (user navigated away), timed out, the device is offline, or the
   * server answered without CORS headers. Angular puts the raw XHR ProgressEvent
   * in `error.error` for those, which is what used to reach the toast as
   * "[object ProgressEvent]". JSON bodies used to show as "[object Object]".
   */
  static toMessage(e: any): string {
    if (!e || e.ok === true) {
      return '';
    }

    if (e.status === 0) {
      const event = e.error;
      if (typeof ProgressEvent !== 'undefined' && event instanceof ProgressEvent && event.type === 'abort') {
        return ''; // Cancelled by navigation or teardown, not a failure worth a toast.
      }
      const offline = typeof navigator !== 'undefined' && navigator.onLine === false;
      return offline ? OFFLINE_MESSAGE : UNREACHABLE_MESSAGE;
    }

    return ErrorHandlerService.bodyMessage(e) || GENERIC_MESSAGE;
  }

  /** The server's own message from the response body, or '' when it sent none. */
  static bodyMessage(e: any): string {
    const body = e?.error;
    if (typeof body === 'string') {
      const text = body.trim();
      // A whole HTML error page (IIS / ASP.NET) is not something to put in a toast.
      return text && !text.startsWith('<') ? text : '';
    }
    if (body && typeof body === 'object') {
      const candidate =
        body.error_description ?? body.message ?? body.Message ?? body.title ??
        (typeof body.error === 'string' ? body.error : undefined);
      if (typeof candidate === 'string' && candidate.trim()) {
        return candidate.trim();
      }
    }
    return '';
  }

  showServerError(e: any) {
    const message = ErrorHandlerService.toMessage(e);
    if (message) {
      this.toastrService.error(message);
    }
  }
}
